import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'
import {
  userServices,
  agentServices,
  utilServices,
  bookingServices,
  locationServices,
  travelAgentServices,
  serviceVoucherServices,
  invoiceServices
} from '../services'

Vue.use(Vuex)

const vuexLocalStorage = new VuexPersist(
  {
    key: 'vuex',
    storage: window.localStorage,
    reducer: state => ({
      user: state.user
    })
  }
)

const UtilModule = {
  namespaced: true,
  state: {
    countries: null
  },
  getters: {
    countries: (state) => {
      return state.countries
    },
    select2Countries: (state) => {
      let countries = state.countries

      if (countries) {
        return Object.keys(countries).map(key => {
          return {
            value: key,
            label: countries[key]
          }
        })
      } else {
        return []
      }
    }
  },
  mutations: {
    setCountries (state, countries) {
      state.countries = countries
    }
  },
  actions: {
    getCountries ({ commit }) {
      let res = utilServices.countries()
      res.then(
        (response) => {
          commit('setCountries', response.data)
        },
        (e) => {
          console.log(e)
        }
      )
      return res
    }
  }
}

const UserModule = {
  namespaced: true,
  state: {
    accessToken: ''
  },
  getters: {
    accessToken: state => state.accessToken,
    jwtData: (state, getters) => state.accessToken ? JSON.parse(atob(getters.accessToken.split('.')[1])) : null,
    jwtSubject: (state, getters) => getters.jwtData ? getters.jwtData.sub : null,
    jwtIssuer: (state, getters) => getters.jwtData ? getters.jwtData.iss : null,
    isUserLoggedIn: (state) => !!state.accessToken
  },
  mutations: {
    setJWT (state, jwt) {
      state.accessToken = jwt
    },
    unsetJWT (state) {
      state.accessToken = null
    }
  },
  actions: {
    setBearerToken ({ getters }) {
      userServices.setBearerToken(getters.accessToken)
    },
    login ({ commit }, payload) {
      let res = userServices.login(payload)
      res.then(
        (response) => {
          commit('setJWT', response.data.access_token)
        },
        (e) => {
          console.log(e)
        }
      )
      return res
    },
    logout ({ commit }) {
      let res = userServices.logout()
      res.then(
        (response) => {
          commit('unsetJWT')
        },
        (e) => {
          console.log(e)
        }
      )
      return res
    },
    validateToken ({ getters, commit }) {
      let jwtData = getters.jwtData
      if (jwtData && jwtData.hasOwnProperty('exp') && jwtData.exp < Math.floor((Date.now() / 1000))) {
        commit('unsetJWT')
      }
    },
    refreshToken ({ commit }) {
      let res = userServices.refresh()
      res.then(
        (response) => {
          commit('setJWT', response.data.access_token)
        },
        (e) => {
          console.log(e)
        }
      )
      return res
    }
  }
}

const AppModule = {
  namespaced: true,
  state: {
    log: []
  },
  getters: {
    log: state => state.log,
    latestLog: (state) => {
      let pos = state.log.length - 1
      return state.log[pos]
    }
  },
  mutations: {
    addLogOutNotice (state, payload) {
      state.log.push({
        ...payload,
        'type': 'warning'
      })
    },
    addErrorLog (state, payload) {
      state.log.push({
        ...payload,
        'type': 'error',
        'timeout': 10,
        'timestamp': Date.now()
      })
    },
    addSuccessLog (state, payload) {
      state.log.push({
        ...payload,
        'type': 'success',
        'timeout': 10,
        'timestamp': Date.now()
      })
    },
    addValidationError (state, message) {
      state.log.push({
        message,
        'type': 'error',
        'timeout': 10,
        'timestamp': Date.now()
      })
    },
    addStoreSuccess (state, message) {
      state.log.push({
        message,
        'type': 'success',
        'timeout': 10,
        'timestamp': Date.now()
      })
    },
    addStoreFail (state, e) {
      state.log.push({
        'errors': e.errors,
        'message': e.message,
        'type': 'error',
        'timeout': 10,
        'timestamp': Date.now()
      })
    },
    addIndexFail (state, e) {
      state.log.push({
        'errors': e.errors,
        'message': e.message,
        'type': 'error',
        'timeout': 10,
        'timestamp': Date.now()
      })
    },
    addIndexSuccess (state, message) {
      state.log.push({
        message,
        'type': 'success',
        'timeout': 10,
        'timestamp': Date.now()
      })
    }
  },
  actions: {
    addValidationError ({ commit }, message) {
      commit('addValidationError', message)
    },
    addErrorLog ({ commit }, payload) {
      commit('addErrorLog', payload)
    },
    addSuccessLog ({ commit }, payload) {
      commit('addSuccessLog', payload)
    },
    addLogOutNotice ({ commit }, payload) {
      commit('addLogOutNotice', payload)
    }
  }
}

const BookingsModule = {
  namespaced: true,
  state: {
    currentBooking: null,
    bookingsList: []
  },
  getters: {
    currentBooking: state => {
      return state.currentBooking
    },
    bookingsList: state => {
      return state.bookingsList
    },
    bookingById: (state) => (id) => {
      return state.bookings.find(
        (booking) => {
          return booking.id === id
        }
      )
    }
  },
  mutations: {
    setBookingsList (state, bookingsList) {
      state.bookingsList = bookingsList
    },
    setCurrentBooking (state, booking) {
      state.currentBooking = booking
    }
  },
  actions: {
    store ({ commit }, payload) {
      let res = bookingServices.store(payload)
      res.then(
        (response) => {
          commit('app/addStoreSuccess', 'Booking added successfully!', { root: true })
          console.log(response)
        }
      ).catch((e) => {
        commit('app/addStoreFail', e.response.data, { root: true })
      })
      return res
    },
    import ({ commit }, payload) {
      let res = bookingServices.import(payload)
      res.then(
        (response) => {
          commit('app/addStoreSuccess', 'Booking added successfully!', { root: true })
          console.log(response)
        }
      ).catch((e) => {
        commit('app/addStoreFail', e.response.data, { root: true })
      })
      return res
    },
    update ({ commit }, payload) {
      let res = bookingServices.update(payload.id, payload)
      res.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'booking/update',
            message: 'Booking updated successfully!'
          }, { root: true })
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'booking/update',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    index ({ commit }) {
      let res = bookingServices.index()
      res.then(
        (response) => {
          commit('setBookingsList', response.data)
        }
      ).catch((e) => {
        commit('app/addIndexFail', e.response.data, { root: true })
      })
      return res
    },
    show ({ commit }, bookingId) {
      let res = bookingServices.show(bookingId)
      res.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'booking/show',
            message: 'Booking data loaded!'
          }, { root: true })
          commit('setCurrentBooking', response.data.data)
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'booking/show',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    destroy ({ commit }, bookingId) {
      let xhr = bookingServices.destroy(bookingId)
      xhr.then(() => {
        commit('app/addSuccessLog', {
          origin: 'booking/delete',
          message: 'Booking Deleted'
        }, { root: true })
      }).catch(
        (e) => {
          commit('app/addErrorLog', {
            origin: 'booking/delete',
            ...e.response.data
          }, { root: true })
        }
      )
      return xhr
    }
  }
}

const AgentsModule = {
  namespaced: true,
  state: {
    currentAgent: null,
    agents: []
  },
  getters: {
    currentAgent: state => {
      return state.currentAgent
    },
    agents: state => {
      return state.agents
    },
    agentById: (state) => (id) => {
      return state.agents.find(
        (agent) => {
          return agent.id === id
        }
      )
    },
    select2Agents: (state) => {
      if (state.agents.length) {
        return state.agents.map(agent => {
          return {
            id: agent.id,
            label: agent.name
          }
        })
      } else {
        return []
      }
    }
  },
  mutations: {
    setAgents (state, agents) {
      state.agents = agents
    },
    setCurrentAgent (state, agent) {
      state.currentAgent = agent
    }
  },
  actions: {
    store ({ commit }, payload) {
      let res = agentServices.store(payload)
      res.then(
        (response) => {
          commit('app/addStoreSuccess', 'Agent added successfully!', { root: true })
          console.log(response)
        }
      ).catch((e) => {
        commit('app/addStoreFail', e.response.data, { root: true })
      })
      return res
    },
    update ({ commit }, payload) {
      let res = agentServices.update(payload.id, payload)
      res.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'agent/update',
            message: 'Agent updated successfully!'
          }, { root: true })
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'agent/update',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    index ({ commit }) {
      let res = agentServices.index()
      res.then(
        (response) => {
          commit('setAgents', response.data.data)
          commit('app/addIndexSuccess', 'Agents listed successfully!', { root: true })
        }
      ).catch((e) => {
        commit('app/addIndexFail', e.response.data, { root: true })
      })
      return res
    },
    show ({ commit }, agentId) {
      let res = agentServices.show(agentId)
      res.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'agent/show',
            message: 'Agent data loaded!'
          }, { root: true })
          commit('setCurrentAgent', response.data.data)
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'agent/show',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    destroy ({ commit }, agentId) {
      let xhr = agentServices.destroy(agentId)
      xhr.then(() => {
        commit('app/addSuccessLog', {
          origin: 'agent/delete',
          message: 'Agent Deleted'
        }, { root: true })
      }).catch(
        (e) => {
          commit('app/addErrorLog', {
            origin: 'agent/delete',
            ...e.response.data
          }, { root: true })
        }
      )
      return xhr
    }
  }
}

const InvoiceModule = {
  namespaced: true,
  state: {
    invoiceList: [],
    currentInvoice: null
  },
  getters: {
    invoiceList: state => {
      return state.invoiceList
    },
    currentInvoice: state => {
      return state.currentInvoice
    }
  },
  mutations: {
    setInvoiceList (state, response) {
      state.invoiceList = response
    },
    setCurrentInvoice (state, response) {
      state.currentInvoice = response
    }
  },
  actions: {
    destroy ({ commit }, invoiceId) {
      let xhr = invoiceServices.destroy(invoiceId)
      xhr.then(() => {
        commit('app/addSuccessLog', {
          origin: 'invoice/delete',
          message: 'Invoice Deleted'
        }, { root: true })
      }).catch(
        (e) => {
          commit('app/addErrorLog', {
            origin: 'invoice/delete',
            ...e.response.data
          }, { root: true })
        }
      )
      return xhr
    },
    index ({ commit }) {
      let res = invoiceServices.index()
      res.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'invoice/index',
            message: 'Invoices loaded!'
          }, { root: true })
          commit('setInvoiceList', response.data.data)
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'invoice/index',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    show ({ commit }, invoiceId) {
      let res = invoiceServices.show(invoiceId)
      res.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'invoice/show',
            message: 'Invoice loaded!'
          }, { root: true })
          commit('setCurrentInvoice', response.data.data)
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'invoice/show',
          ...e.response.data
        }, { root: true })
      })
      return res
    }
  }
}

const LocationsModule = {
  namespaced: true,
  state: {
    locations: []
  },
  getters: {
    locations: state => {
      return state.locations
    },
    locationById: (state) => (id) => {
      if (state.locations) {
        return state.locations.find(
          (location) => {
            return location.id === id
          }
        )
      } else {
        return null
      }
    },
    select2Locations: (state) => {
      if (state.locations.length) {
        return state.locations.map(location => {
          return {
            id: location.id,
            label: location.name
          }
        })
      } else {
        return []
      }
    }
  },
  mutations: {
    setLocations (state, locations) {
      state.locations = locations
    }
  },
  actions: {
    index ({ commit }) {
      let res = locationServices.index()
      res.then(
        (response) => {
          commit('setLocations', response.data.data)
        }
      ).catch((e) => {
        commit('app/addIndexFail', e.response.data, { root: true })
      })
      return res
    },
    store ({ commit, dispatch }, payload) {
      let xhr = locationServices.store(payload)
      xhr.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'locations/store',
            message: 'Location added successfully!'
          }, { root: true })
          dispatch('index')
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'locations/store',
          ...e.response.data
        }, { root: true })
      })
      return xhr
    }
  }
}

const TravelAgentsModule = {
  namespaced: true,
  state: {
    travelAgents: []
  },
  getters: {
    travelAgents: state => {
      return state.travelAgents
    },
    select2TravelAgents: (state) => {
      if (state.travelAgents.length) {
        let travelagents = state.travelAgents.map(travelAgent => {
          return {
            id: travelAgent.id,
            label: travelAgent.name
          }
        })

        let _default = { id: null, label: 'Select Travel Agent' }

        return [..._default, ...travelagents]
      } else {
        return []
      }
    }
  },
  mutations: {
    setTravelAgents (state, travelAgents) {
      state.travelAgents = travelAgents
    }
  },
  actions: {
    index ({ commit }) {
      let res = travelAgentServices.index()
      res.then(
        (response) => {
          commit('setTravelAgents', response.data.data)
        }
      ).catch((e) => {
        commit('app/addIndexFail', e.response.data, { root: true })
      })
      return res
    },
    store ({ commit, dispatch }, payload) {
      let xhr = travelAgentServices.store(payload)
      xhr.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'travelAgents/store',
            message: 'Agent added successfully!'
          }, { root: true })
          dispatch('index')
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'travelAgents/store',
          ...e.response.data
        }, { root: true })
      })
      return xhr
    }
  }
}

const ServiceVoucherModule = {
  namespaced: true,
  state: {
    currentServiceVoucher: null,
    serviceVouchers: []
  },
  getters: {
    serviceVouchers: state => {
      return state.serviceVouchers
    },
    currentServiceVoucher: state => {
      return state.currentServiceVoucher
    }
  },
  mutations: {
    setServiceVouchers (state, serviceVouchers) {
      state.serviceVouchers = serviceVouchers
    },
    setCurrentServiceVoucher (state, agent) {
      state.currentServiceVoucher = agent
    }
  },
  actions: {
    index ({ commit }) {
      let res = serviceVoucherServices.index()
      res.then(
        (response) => {
          commit('setServiceVouchers', response.data.data)
        }
      ).catch((e) => {
        commit('app/addIndexFail', e.response.data, { root: true })
      })
      return res
    },
    store ({ commit }, payload) {
      let res = serviceVoucherServices.store(payload)
      res.then(
        (response) => {
          commit('app/addStoreSuccess', 'Service Voucher added successfully!', { root: true })
          console.log(response)
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'serviceVoucher/store',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    update ({ commit }, payload) {
      let res = serviceVoucherServices.update(payload.id, payload)
      res.then(
        () => {
          commit('app/addStoreSuccess', 'Service Voucher updated successfully!', { root: true })
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'serviceVoucher/update',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    show ({ commit }, serviceVoucherId) {
      let res = serviceVoucherServices.show(serviceVoucherId)
      res.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'serviceVoucher/show',
            message: 'Service Voucher data loaded!'
          }, { root: true })
          commit('setCurrentServiceVoucher', response.data.data)
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'serviceVoucher/show',
          ...e.response.data
        }, { root: true })
      })
      return res
    },
    destroy ({ commit }, bookingId) {
      let xhr = serviceVoucherServices.destroy(bookingId)
      xhr.then(
        (response) => {
          commit('app/addSuccessLog', {
            origin: 'serviceVouchers/destroy',
            message: 'Service deleted successfully!'
          }, { root: true })
        }
      ).catch((e) => {
        commit('app/addErrorLog', {
          origin: 'serviceVouchers/destroy',
          ...e.response.data
        }, { root: true })
      })
      return xhr
    }
  }
}

export default new Vuex.Store(
  {
    modules: {
      util: UtilModule,
      app: AppModule,
      bookings: BookingsModule,
      agents: AgentsModule,
      invoices: InvoiceModule,
      user: UserModule,
      locations: LocationsModule,
      travelAgents: TravelAgentsModule,
      serviceVouchers: ServiceVoucherModule
    },
    plugins: [vuexLocalStorage.plugin]
  }
)



// WEBPACK FOOTER //
// ./src/store/index.js